import React from 'react'
import { Box, Card, Typography, Grid, CircularProgress, Alert } from '@mui/material'
import { fetchSummaryByType } from '../../api/summaryApi'

const ENTITY_LABELS = {
  university: 'University',
  course: 'Course',
  specialization: 'Specialization',
}

const KEY_ALIASES = {
  live: ['approved'],
  pending: ['pending'],
  resubmitted: ['re-submitted', 'resubmitted'],
  rework: ['rework'],
  draft: ['draft'],
}

function buildStaticList(entityLabel = 'Entity',isAdmin) {
  return [
    { key: 'all', label: `All ${entityLabel}`, count: 0 },
    { key: 'live', label: `Live ${entityLabel}`, count: 0 },
   ...(!isAdmin ? [ { key: 'pending', label: 'Pending Approval', count: 0 },
    { key: 'resubmitted', label: 'Re-submitted', count: 0 },
    { key: 'rework', label: 'Rework', count: 0 },
    { key: 'draft', label: 'Draft', count: 0 },]:[])
  ]
}

const norm = v => String(v ?? '').toLowerCase().trim()

function resolveBucketKey(apiKey) {
  const k = norm(apiKey)
  for (const [bucket, aliases] of Object.entries(KEY_ALIASES)) {
    if (aliases.some(a => norm(a) === k)) return bucket
  }
  return null
}

export default function UniversitySummary({
  type = 'university',
  isAdmin = false,
  fetcher = fetchSummaryByType,
  onError,
  sx,
  requestParams = {},
  isRender,
}) {
  const entityLabel = ENTITY_LABELS[type] || 'Entity'
  const [data, setData] = React.useState(buildStaticList(entityLabel,isAdmin))
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')

    fetcher(type, { isAdmin, params: requestParams })
      .then(items => {
        if (!mounted) return

        const bucketCounts = {
          live: 0,
          pending: 0,
          resubmitted: 0,
          rework: 0,
          draft: 0,
        }

        let totalAll = 0

        for (const it of items || []) {
          const count = Number(it.count ?? it.total ?? 0)
          totalAll += count
          const statusKey = it.key ?? it.status ?? it.label ?? it.name
          const bucket = resolveBucketKey(statusKey)
          if (bucket && bucket in bucketCounts) {
            bucketCounts[bucket] += count
          }
        }

        const merged = buildStaticList(entityLabel,isAdmin).map(card => {
          if (card.key === 'all') return { ...card, count: totalAll }
          return { ...card, count: bucketCounts[card.key] ?? 0 }
        })

        setData(merged)
      })
      .catch(err => {
        if (!mounted) return
        const msg = err?.message || 'Failed to load summary'
        setError(msg)
        onError?.(err)
      })
      .finally(() => { if (mounted) setLoading(false) })

    return () => { mounted = false }
  }, [type, isAdmin, fetcher, onError, entityLabel, JSON.stringify(requestParams),isRender])

  return (
    <Box sx={{ p: 2, position: 'relative', ...sx }}>
      {loading && (!data || data.length === 0) ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Grid container spacing={2}>
          {data.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 2, lg: 2 }} key={item.key ?? item.label ?? index}>
              <Card
                elevation={2}
                sx={{
                  py: 1,
                  px: 0,
                  textAlign: 'center',
                  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.10)',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                }}
              >
                <Typography sx={{ fontSize: '18px' }} color="#363853">
                  {item.count}
                </Typography>
                <Typography sx={{ fontSize: '13px' }} color="#6E7485">
                  {item.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}
